// 与 App 端 @yr-panel/utils 的 crcData 保持一致：UTF-8 字节做 CRC16（反射多项式 0x8408），输出高字节在前的 hex
const CRC_TABLE = (() => {
    const table = new Uint16Array(256)
    for (let n = 0; n < 256; n++) {
        let c = n
        for (let k = 0; k < 8; k++) {
            c = c & 1 ? (c >>> 1) ^ 0x8408 : c >>> 1
        }
        table[n] = c
    }
    return table
})()

export function crcModelId(model) {
    if (!model) return ''
    const bytes = new TextEncoder().encode(model)
    let crc = 0
    for (const b of bytes) {
        crc = CRC_TABLE[(crc ^ b) & 0xff] ^ (crc >> 8)
    }
    crc = ~crc & 0xffff
    return [crc >> 8, crc & 0xff].map((v) => v.toString(16).padStart(2, '0')).join('')
}

// 图片类型来自后台字典（meta.image_types），meta 未加载时为空数组
export function imageTypes(meta) {
    return (meta && meta.image_types) || []
}

// 文案用途的挂载位置：新数据看 mount，旧数据只有 guide（guide=true 即挂在方案上）
export function textTypeMount(t) {
    if (!t) return ''
    if (typeof t.mount === 'string') return t.mount
    return t.guide ? 'scheme' : ''
}

// 挂在某类对象上的文案用途（meta.text_types），meta 未加载时为空数组
export function mountedTextTypes(textTypes, mount) {
    return (textTypes || []).filter((t) => textTypeMount(t) === mount)
}

// 方案引导文案项：挂载位置为 scheme 的文案用途
export function guideFields(meta) {
    return mountedTextTypes(meta && meta.text_types, 'scheme').map((t) => ({ key: t.value, label: t.label }))
}

// 挂载文案 { 用途code: TextRef }：去掉空值，返回新对象
export function cleanMountedTexts(texts) {
    const out = {}
    Object.keys(texts || {}).forEach((k) => {
        const v = texts[k]
        if (!v) return
        if (typeof v === 'object' && !v.default && !textRefVariants(v).length) return
        out[k] = v
    })
    return out
}

// 文案库：按用途分组，供各处下拉选择
export function groupTextsByType(list) {
    const groups = {}
    ;(list || []).forEach((t) => {
        (groups[t.type] = groups[t.type] || []).push(t)
    })
    return groups
}

// 文案预览：优先中文，其次英文，截断避免下拉过宽
export function textPreview(text, max = 24) {
    const texts = (text && text.texts) || {}
    const s = (texts.zh || texts.en || '').replace(/\s+/g, ' ').trim()
    return s.length > max ? s.slice(0, max) + '…' : s
}

// 下拉选项文案：备注 + 预览
export function textOptionLabel(text) {
    const preview = textPreview(text)
    const remark = text.remark || text.code
    return preview ? `${remark}｜${preview}` : remark
}

export function emptyScheme(pairingModelCode) {
    return {
        code: '',
        pairing_model_code: pairingModelCode,
        name: '',
        i18n_key: '',
        description: '',
        model_id_override: '',
        ble_match: true,
        hide_in_list: false,
        test_only: false,
        group_tip: '',
        images: [],
        guide: {},
        led_config: { has_led: false, reset_color: '', status: [] },
        functions: [],
    }
}

// 灯效说明正文下方的按钮，空表示不显示
export const LED_ACTIONS = [
    { value: '', label: '无' },
    { value: 'repair', label: '重新配网' },
]

export function ledActionLabel(value) {
    return (LED_ACTIONS.find((a) => a.value === (value || '')) || {}).label || value
}

// 条件文案内置变量，值为布尔
// 条件文案的内置条件。单频/双频由方案决定，不作为条件；其它场景用「自定义…」填变量名
export const TEXT_REF_VARS = [
    { value: 'bound', label: '已绑定' },
    { value: 'owner', label: '主账号' },
]

export const TEXT_REF_VAR_PATTERN = /^[a-z][a-z0-9_]{0,31}$/

// 文案引用（TextRef）：字符串为文案 code；对象为 { default, variants: [{ when, text }] }
export function textRefDefault(ref) {
    if (!ref) return ''
    if (typeof ref === 'string') return ref
    return ref.default || ''
}

export function textRefVariants(ref) {
    return (ref && typeof ref === 'object' && Array.isArray(ref.variants)) ? ref.variants : []
}

// 与后台校验一致：有条件文案时默认文案必填，每条至少一个条件、变量名合法、文案必选；返回错误信息，合法时为空串
export function validateTextRef(ref) {
    const variants = textRefVariants(ref)
    if (!variants.length) return ''
    if (!textRefDefault(ref)) return '有条件文案时默认文案必填'
    for (const v of variants) {
        const keys = Object.keys(v.when || {})
        if (!keys.length) return '条件文案至少需要一个条件'
        const bad = keys.find((k) => !TEXT_REF_VAR_PATTERN.test(k))
        if (bad !== undefined) return `条件变量名「${bad}」不合法，需小写字母开头，只含小写字母、数字、下划线，最多 32 位`
        if (keys.some((k) => v.when[k] === '' || v.when[k] === null || v.when[k] === undefined)) return '条件的值不能为空'
        if (!v.text) return '请为每条条件文案选择文案'
    }
    return ''
}

function conditionText(when) {
    return Object.keys(when || {}).map((k) => {
        const name = (TEXT_REF_VARS.find((x) => x.value === k) || {}).label || k
        const v = when[k]
        return typeof v === 'boolean' ? `${name}=${v ? '是' : '否'}` : `${name}=${v}`
    }).join(' 且 ')
}

// 表格里的简短展示：默认文案 + 条件数量；labelOf(code) 把文案 code 转成显示名
export function textRefSummary(ref, labelOf) {
    const def = textRefDefault(ref)
    const variants = textRefVariants(ref)
    const head = def ? labelOf(def) : ''
    if (!variants.length) return head
    const tail = variants.map((v) => `${conditionText(v.when)} → ${v.text ? labelOf(v.text) : '?'}`).join('；')
    return `${head || '（无默认）'}；${tail}`
}

function escapeHtml(s) {
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;')
}

// 富文本预览：先转义 HTML，再把 **x** 渲染为加粗、{name} 渲染为占位符标签、换行渲染为 <br>，结果可直接用于 v-html
export function richTextPreviewHtml(text) {
    return escapeHtml(text || '')
        .replace(/\*\*([\s\S]+?)\*\*/g, '<b>$1</b>')
        .replace(/\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g, '<span class="rich-ph">$1</span>')
        .replace(/\r?\n/g, '<br>')
}
